import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faMagnifyingGlass, faPencil, faSquareMinus, faSquarePlus } from "@fortawesome/free-solid-svg-icons";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { normalizar } from "../../common/stringNormalizer.js";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const ARGENTINA_CENTER = [-38.4161, -63.6167];

const getAddressName = (address = {}) =>
  address.city ||
  address.town ||
  address.village ||
  address.municipality ||
  address.hamlet ||
  address.suburb ||
  address.county ||
  address.state ||
  "";

const getPostalCode = (address = {}) => address.postcode || "";

const MapClickHandler = ({ onSelect }) => {
  useMapEvents({
    click: async (event) => {
      const { lat, lng } = event.latlng;
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
        );
        const data = await response.json();
        onSelect({
          lat,
          lng,
          nombre: getAddressName(data.address) || data.name || data.display_name || "",
          codigoPostal: getPostalCode(data.address),
          displayName: data.display_name || "",
        });
      } catch (error) {
        console.error("Error al obtener ubicacion:", error);
      }
    },
  });

  return null;
};

const RecenterMap = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 120);
  }, [map]);

  useEffect(() => {
    if (position) {
      map.setView(position, 12);
    }
  }, [map, position]);

  return null;
};

const ZonaMapModal = ({ onClose, onConfirm }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const markerPosition = selected ? [selected.lat, selected.lng] : null;

  const buscarUbicacion = async () => {
    if (!search.trim()) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=ar&q=${encodeURIComponent(search)}`
      );
      const data = await response.json();
      setResults(data || []);
    } catch (error) {
      console.error("Error al buscar ubicacion:", error);
      setResults([]);
    }
  };

  const seleccionarResultado = (result) => {
    setSelected({
      lat: Number(result.lat),
      lng: Number(result.lon),
      nombre: getAddressName(result.address) || result.name || result.display_name || "",
      codigoPostal: getPostalCode(result.address),
      displayName: result.display_name || "",
    });
    setResults([]);
  };

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={onClose}></div>
      <div className="modal-card" style={{ width: "min(980px, 94vw)" }}>
        <header className="modal-card-head">
          <p className="modal-card-title">Seleccionar localidad en mapa</p>
          <button className="delete" aria-label="close" onClick={onClose}></button>
        </header>
        <section className="modal-card-body">
          <div className="field has-addons">
            <div className="control is-expanded">
              <input
                className="input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    buscarUbicacion();
                  }
                }}
                placeholder="Buscar localidad, provincia o direccion"
              />
            </div>
            <div className="control">
              <button type="button" className="button is-info" onClick={buscarUbicacion} title="Buscar">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </button>
            </div>
          </div>

          {results.length > 0 && (
            <div style={{ border: "1px solid #d1d5db", borderRadius: 6, marginBottom: 10, maxHeight: 140, overflowY: "auto" }}>
              {results.map((result) => (
                <button
                  type="button"
                  key={`${result.place_id}-${result.lat}-${result.lon}`}
                  className="button is-white is-fullwidth"
                  style={{ justifyContent: "flex-start", whiteSpace: "normal", height: "auto", minHeight: 36, textAlign: "left" }}
                  onClick={() => seleccionarResultado(result)}
                >
                  {result.display_name}
                </button>
              ))}
            </div>
          )}

          <div style={{ height: 420, borderRadius: 8, overflow: "hidden", border: "1px solid #d1d5db" }}>
            <MapContainer center={ARGENTINA_CENTER} zoom={5} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapClickHandler onSelect={setSelected} />
              <RecenterMap position={markerPosition} />
              {markerPosition && <Marker position={markerPosition} />}
            </MapContainer>
          </div>

          {selected && (
            <div className="notification is-light mt-3" style={{ padding: "0.75rem 1rem" }}>
              <strong>{selected.nombre || "Ubicacion seleccionada"}</strong>
              <br />
              {selected.displayName}
              {selected.codigoPostal && <div>Codigo postal: {selected.codigoPostal}</div>}
            </div>
          )}
        </section>
        <footer className="modal-card-foot">
          <button
            type="button"
            className="button is-success"
            disabled={!selected}
            onClick={() => onConfirm(selected)}
          >
            Usar seleccion
          </button>
          <button type="button" className="button" onClick={onClose}>Cancelar</button>
        </footer>
      </div>
    </div>
  );
};

const ZonasGeograficas = () => {
  const [zonas, setZonas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("activas");
  const [form, setForm] = useState({
    IdTipoZonaGeografica: "",
    Nombre: "",
    CodigoPostal: "",
    Dependencia: "0",
  });
  const [selectedZona, setSelectedZona] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapTarget, setMapTarget] = useState("create");

  useEffect(() => {
    getZonas();
    getTipos();
  }, []);

  const tipoLocalidadId = useMemo(() => {
    const localidad = tipos.find((tipo) => normalizar(tipo.Nombre || "") === "localidad");
    return localidad?.IdTipoZonaGeografica || "";
  }, [tipos]);

  useEffect(() => {
    if (!form.IdTipoZonaGeografica && tipoLocalidadId) {
      setForm((prev) => ({ ...prev, IdTipoZonaGeografica: String(tipoLocalidadId) }));
    }
  }, [form.IdTipoZonaGeografica, tipoLocalidadId]);

  const zonasDependencia = zonas.filter((zona) => Number(zona.Estado) === 1);

  const buildDependenciaLabel = (zona) => {
    if (!zona || Number(zona.Dependencia) === 0) return "Sin dependencia";
    return [zona.TipoDependencia, zona.DependenciaNombre].filter(Boolean).join(" - ");
  };

  const getZonas = async () => {
    try {
      const response = await api.get("/zonas-geograficas");
      setZonas(response.data || []);
    } catch (error) {
      console.error("Error al obtener zonas geograficas:", error);
    }
  };

  const getTipos = async () => {
    try {
      const response = await api.get("/tipos-zona-geografica");
      setTipos(response.data || []);
    } catch (error) {
      console.error("Error al obtener tipos de zona geografica:", error);
    }
  };

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const aplicarSeleccionMapa = (selected) => {
    const setter = mapTarget === "edit" ? setSelectedZona : setForm;
    setter((prev) => ({
      ...prev,
      Nombre: selected.nombre || prev.Nombre,
      CodigoPostal: selected.codigoPostal || prev.CodigoPostal || "",
    }));
    setShowMapModal(false);
  };

  const createZona = async (e) => {
    e.preventDefault();

    const existe = zonas.some(
      (zona) =>
        normalizar(zona.Nombre || "") === normalizar(form.Nombre || "") &&
        Number(zona.Dependencia || 0) === Number(form.Dependencia || 0) &&
        Number(zona.IdTipoZonaGeografica) === Number(form.IdTipoZonaGeografica)
    );

    if (existe) {
      Swal.fire({
        icon: "warning",
        title: "Duplicado",
        text: "Ya existe una zona geografica con ese nombre, tipo y dependencia.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    try {
      await api.post("/zonas-geograficas", {
        ...form,
        Dependencia: Number(form.Dependencia || 0),
      });
      await getZonas();
      setForm({
        IdTipoZonaGeografica: tipoLocalidadId ? String(tipoLocalidadId) : "",
        Nombre: "",
        CodigoPostal: "",
        Dependencia: "0",
      });
      Swal.fire({
        icon: "success",
        title: "Guardado",
        text: "La zona geografica fue guardada exitosamente.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: error?.response?.data?.msg || "Ocurrio un problema al guardar la zona geografica.",
      });
    }
  };

  const handleEditZona = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/zonas-geograficas/${selectedZona.IdZonaGeografica}`, {
        IdTipoZonaGeografica: selectedZona.IdTipoZonaGeografica,
        Nombre: selectedZona.Nombre,
        CodigoPostal: selectedZona.CodigoPostal,
        Dependencia: Number(selectedZona.Dependencia || 0),
      });
      setSelectedZona(null);
      await getZonas();
      Swal.fire({
        icon: "success",
        title: "Zona actualizada",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.msg || "Ocurrio un error al actualizar la zona geografica.",
      });
    }
  };

  const cambiarEstado = async (idZonaGeografica) => {
    try {
      const zonaActual = zonas.find((zona) => Number(zona.IdZonaGeografica) === Number(idZonaGeografica));
      const nuevoEstado = Number(zonaActual?.Estado) === 1 ? "desactivada" : "activada";
      await api.patch(`/zonas-geograficas/${idZonaGeografica}/toggle`);
      await getZonas();
      Swal.fire({
        icon: "success",
        title: `Zona ${nuevoEstado}`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrio un error al cambiar el estado de la zona geografica.",
      });
    }
  };

  const filtrarZonas = () => {
    if (filtroEstado === "activas") return zonas.filter((zona) => Number(zona.Estado) === 1);
    if (filtroEstado === "inactivas") return zonas.filter((zona) => Number(zona.Estado) === 0);
    return zonas;
  };

  const renderTipoOptions = () =>
    tipos.map((tipo) => (
      <option key={tipo.IdTipoZonaGeografica} value={tipo.IdTipoZonaGeografica}>
        {tipo.Nombre}
      </option>
    ));

  const renderDependenciaOptions = (currentId = null) =>
    zonasDependencia
      .filter((zona) => Number(zona.IdZonaGeografica) !== Number(currentId))
      .map((zona) => (
        <option key={zona.IdZonaGeografica} value={zona.IdZonaGeografica}>
          {[zona.TipoZonaGeografica, zona.Nombre].filter(Boolean).join(" - ")}
        </option>
      ));

  const columnas = [
    {
      name: "Nombre",
      selector: (row) => row.Nombre,
      sortable: true,
      width: "20vw",
    },
    {
      name: "Tipo",
      selector: (row) => row.TipoZonaGeografica,
      sortable: true,
      width: "14vw",
    },
    {
      name: "Codigo postal",
      selector: (row) => row.CodigoPostal || "-",
      sortable: true,
      width: "13vw",
    },
    {
      name: "Dependencia",
      selector: (row) => buildDependenciaLabel(row),
      sortable: true,
      width: "24vw",
      wrap: true,
    },
    {
      cell: (row) => (
        <button
          className="button is-small is-info"
          title="Editar"
          onClick={() => setSelectedZona(row)}
        >
          <FontAwesomeIcon icon={faPencil} />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      compact: true,
    },
    {
      cell: (row) =>
        Number(row.Estado) === 1 ? (
          <FontAwesomeIcon
            icon={faSquarePlus}
            style={{ color: "#37ff00", cursor: "pointer" }}
            size="2xl"
            onClick={() => cambiarEstado(row.IdZonaGeografica)}
          />
        ) : (
          <FontAwesomeIcon
            icon={faSquareMinus}
            style={{ color: "#ff0000", cursor: "pointer" }}
            size="2xl"
            onClick={() => cambiarEstado(row.IdZonaGeografica)}
          />
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      compact: true,
    },
  ];

  const paginationOptionsES = {
    rowsPerPageText: "Filas por pagina",
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsItemText: "Todos",
  };

  return (
    <div className="columns mt-5 is-centered">
      <div>
        <div className="notification is-primary">
          <strong>Ingresa los datos de la zona geografica.</strong>
        </div>

        <form onSubmit={createZona}>
          <div className="field">
            <div className="container">
              <label className="label required">Tipo de zona geografica</label>
              <div className="control">
                <div className="select is-fullwidth">
                  <select
                    value={form.IdTipoZonaGeografica}
                    onChange={(e) => handleChange("IdTipoZonaGeografica", e.target.value)}
                    required
                  >
                    <option value="">Seleccionar tipo</option>
                    {renderTipoOptions()}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="field">
            <div className="container">
              <label className="label required">Nombre</label>
              <div className="field has-addons">
                <div className="control is-expanded">
                  <input
                    type="text"
                    className="input"
                    value={form.Nombre}
                    onChange={(e) => handleChange("Nombre", e.target.value)}
                    placeholder="Nombre"
                    required
                  />
                </div>
                <div className="control">
                  <button
                    type="button"
                    className="button is-info"
                    title="Seleccionar desde mapa"
                    onClick={() => {
                      setMapTarget("create");
                      setShowMapModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faLocationDot} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="field">
            <div className="container">
              <label className="label">Codigo postal</label>
              <div className="control">
                <input
                  type="text"
                  className="input"
                  value={form.CodigoPostal}
                  onChange={(e) => handleChange("CodigoPostal", e.target.value)}
                  placeholder="Codigo postal"
                />
              </div>
            </div>
          </div>

          <div className="field">
            <div className="container">
              <label className="label">Dependencia</label>
              <div className="control">
                <div className="select is-fullwidth">
                  <select
                    value={form.Dependencia}
                    onChange={(e) => handleChange("Dependencia", e.target.value)}
                  >
                    <option value="0">Sin dependencia</option>
                    {renderDependenciaOptions()}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="field mt-3">
            <button type="submit" className="button is-success">Guardar</button>
          </div>
        </form>

        <br />

        <div style={{ border: "1px solid gray", borderRadius: "8px" }}>
          <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
            {["todas", "activas", "inactivas"].map((estado) => (
              <button
                key={estado}
                onClick={() => setFiltroEstado(estado)}
                type="button"
                style={{
                  padding: "6px 16px",
                  border: "none",
                  borderRadius: "20px",
                  backgroundColor: filtroEstado === estado ? "#d1f5d3" : "#f0f2f5",
                  color: filtroEstado === estado ? "#075e54" : "#333",
                  fontWeight: filtroEstado === estado ? "bold" : "normal",
                  boxShadow: filtroEstado === estado ? "inset 0 0 0 1px #1ebea5" : "inset 0 0 0 1px #ccc",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  fontSize: "0.9rem",
                }}
              >
                {estado.charAt(0).toUpperCase() + estado.slice(1)}
              </button>
            ))}
          </div>

          <DataTable
            pagination
            paginationComponentOptions={paginationOptionsES}
            columns={columnas}
            data={filtrarZonas()}
            dense={false}
            responsive={false}
          />
        </div>

        {selectedZona && (
          <div className="modal is-active">
            <div className="modal-background" onClick={() => setSelectedZona(null)}></div>
            <div className="modal-card">
              <header className="modal-card-head">
                <p className="modal-card-title">Editar Zona Geografica</p>
                <button className="delete" aria-label="close" onClick={() => setSelectedZona(null)}></button>
              </header>
              <section className="modal-card-body">
                <form onSubmit={handleEditZona}>
                  <div className="field">
                    <label className="label">Tipo de zona geografica</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedZona.IdTipoZonaGeografica || ""}
                          onChange={(e) => setSelectedZona((prev) => ({ ...prev, IdTipoZonaGeografica: e.target.value }))}
                          required
                        >
                          <option value="">Seleccionar tipo</option>
                          {renderTipoOptions()}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Nombre</label>
                    <div className="field has-addons">
                      <div className="control is-expanded">
                        <input
                          className="input"
                          type="text"
                          value={selectedZona.Nombre || ""}
                          onChange={(e) => setSelectedZona((prev) => ({ ...prev, Nombre: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="control">
                        <button
                          type="button"
                          className="button is-info"
                          title="Seleccionar desde mapa"
                          onClick={() => {
                            setMapTarget("edit");
                            setShowMapModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faLocationDot} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Codigo postal</label>
                    <div className="control">
                      <input
                        className="input"
                        type="text"
                        value={selectedZona.CodigoPostal || ""}
                        onChange={(e) => setSelectedZona((prev) => ({ ...prev, CodigoPostal: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Dependencia</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          value={selectedZona.Dependencia || "0"}
                          onChange={(e) => setSelectedZona((prev) => ({ ...prev, Dependencia: e.target.value }))}
                        >
                          <option value="0">Sin dependencia</option>
                          {renderDependenciaOptions(selectedZona.IdZonaGeografica)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <button type="submit" className="button is-success mr-2">Guardar</button>
                    <button type="button" className="button" onClick={() => setSelectedZona(null)}>Cancelar</button>
                  </div>
                </form>
              </section>
            </div>
          </div>
        )}

        {showMapModal && (
          <ZonaMapModal
            onClose={() => setShowMapModal(false)}
            onConfirm={aplicarSeleccionMapa}
          />
        )}
      </div>
    </div>
  );
};

export default ZonasGeograficas;
